import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import ClientHeader from './ClientHeader';
import AppShell from './shell/AppShell';
import { API_URL } from '../config/api';
import PhoneNumberManager from './PhoneNumberManager';
import ProfileRankWidget from './ProfileRankWidget';

const API = API_URL;
const getToken = () => localStorage.getItem('authToken');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

// Firm logo upload component
const FirmBranding = () => {
  const [logo, setLogo] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const fileRef = React.useRef(null);
  const hdrs = { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' };
  React.useEffect(() => {
    fetch(`${API}/outreach/firm-logo`, { headers: hdrs }).then(r => r.ok ? r.json() : { logo: null }).then(d => setLogo(d.logo)).finally(() => setLoading(false));
  }, []);
  const handleUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Logo must be under 2MB'); return; }
    setSaving(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try { await fetch(`${API}/outreach/firm-logo`, { method: 'PUT', headers: hdrs, body: JSON.stringify({ logo: reader.result }) }); setLogo(reader.result); } catch (err) { alert('Failed to save logo'); }
      finally { setSaving(false); }
    };
    reader.readAsDataURL(file);
  };
  const removeLogo = async () => {
    setSaving(true);
    try { await fetch(`${API}/outreach/firm-logo`, { method: 'PUT', headers: hdrs, body: JSON.stringify({ logo: null }) }); setLogo(null); } catch (err) { alert('Failed'); }
    finally { setSaving(false); }
  };
  if (loading) return <div className="py-3 text-center text-xs text-gray-400">Loading...</div>;
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-900">Firm Branding</h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">Logo for status report PDFs and client exports</p>
      {logo ? (
        <div className="flex items-center gap-4">
          <div className="border rounded-lg p-3 bg-gray-50"><img src={logo} alt="Logo" className="max-h-10 max-w-[160px] object-contain" /></div>
          <div className="flex flex-col gap-1">
            <button onClick={() => fileRef.current?.click()} disabled={saving} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50">Replace</button>
            <button onClick={removeLogo} disabled={saving} className="text-xs text-red-500 hover:text-red-700 disabled:opacity-50">Remove</button>
          </div>
        </div>
      ) : (
        <button onClick={() => fileRef.current?.click()} disabled={saving}
          className="w-full py-4 border-2 border-dashed rounded-lg text-xs text-gray-400 hover:text-gray-600 hover:border-gray-400 disabled:opacity-50">
          {saving ? 'Uploading...' : 'Click to upload logo (PNG, JPG, SVG — max 2MB)'}
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={handleUpload} className="hidden" />
    </div>
  );
};

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Avatar upload state. `avatarPreview` holds the base64 image the
  // user just picked (before the server confirms it), so we can render
  // it optimistically. `uploadingAvatar` guards double-submits.
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarFileRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isClientRoute = location.pathname.startsWith('/client');

  const activeTab = searchParams.get('tab') || 'profile';
  const setActiveTab = (tab) => setSearchParams(tab === 'profile' ? {} : { tab });

  const [partnerships, setPartnerships] = useState([]);
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [connectingCalendar, setConnectingCalendar] = useState(null);
  const [pending, setPending] = useState({ sent: [], received: [] });
  const [partnersLoading, setPartnersLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);

  // Notification preferences
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);

  const acceptToken = searchParams.get('accept');

  useEffect(() => { loadUserData(); loadCalendarConnections(); }, []);
  useEffect(() => { if (activeTab === 'partners' && user) loadPartnerships(); }, [activeTab, user]);
  useEffect(() => { if (activeTab === 'notifications' && user && !notifPrefs) loadNotifPrefs(); }, [activeTab, user]);
  useEffect(() => { if (acceptToken && user) handleAcceptByToken(acceptToken); }, [acceptToken, user]);
  useEffect(() => {
    const granted = searchParams.get('admin_consent');
    const consentErr = searchParams.get('admin_consent_error');
    if (granted === 'granted') {
      setSuccess('Microsoft is now approved for your entire organization. Your team can connect without further admin prompts.');
    } else if (consentErr) {
      setError(`Organization approval was not completed (${consentErr}). Please try again or contact your Microsoft administrator.`);
    }
    if (granted || consentErr) {
      const next = new URLSearchParams(searchParams);
      next.delete('admin_consent'); next.delete('admin_consent_error'); next.delete('tenant');
      setSearchParams(next, { replace: true });
    }
  }, []);

  const loadCalendarConnections = async () => {
    try {
      const res = await fetch(`${API}/calendar/connections`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (res.ok) setCalendarConnections(await res.json());
    } catch {}
  };

  const connectCalendar = async (provider) => {
    setConnectingCalendar(provider);
    try {
      const returnUrl = `${location.pathname}?tab=${activeTab}`;
      const res = await fetch(`${API}/calendar/${provider}/authorize?returnUrl=${encodeURIComponent(returnUrl)}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch { setConnectingCalendar(null); }
  };

  const [connectingOrg, setConnectingOrg] = useState(false);
  const grantOrgConsent = async () => {
    setConnectingOrg(true);
    try {
      const returnUrl = `${location.pathname}?tab=${activeTab}`;
      const res = await fetch(`${API}/calendar/microsoft/admin-consent?returnUrl=${encodeURIComponent(returnUrl)}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setConnectingOrg(false);
    } catch { setConnectingOrg(false); }
  };

  const disconnectCalendar = async (provider) => {
    if (!window.confirm(`Disconnect ${provider === 'google' ? 'Google Calendar' : 'Microsoft Outlook'}?`)) return;
    try {
      await fetch(`${API}/calendar/connections/${provider}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
      setCalendarConnections(prev => prev.filter(c => c.provider !== provider));
    } catch {}
  };

  const loadNotifPrefs = async () => {
    setNotifLoading(true);
    try {
      const res = await axios.get(`${API}/digest/preferences`, { headers: headers() });
      setNotifPrefs(res.data);
    } catch (err) {
      console.error('Failed to load notification preferences:', err);
      // Set defaults so UI still renders
      setNotifPrefs({
        digest_frequency: user?.user_type === 'client' ? 'weekly' : 'daily',
        digest_day: 'friday', digest_time: '09:00', digest_timezone: 'America/New_York',
        event_types: {}, user_type: user?.user_type
      });
    } finally {
      setNotifLoading(false);
    }
  };

  const saveNotifPrefs = async (updates) => {
    setNotifSaving(true);
    try {
      await axios.put(`${API}/digest/preferences`, updates, { headers: headers() });
      const res = await axios.get(`${API}/digest/preferences`, { headers: headers() });
      setNotifPrefs(res.data);
    } catch (err) { setError('Failed to save notification preferences'); }
    finally { setNotifSaving(false); }
  };

  const loadUserData = () => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    setFormData({ name: userData.name || '', email: userData.email || '' });
    setLoading(false);
  };

  const loadPartnerships = async () => {
    setPartnersLoading(true);
    try {
      const [activeRes, pendingRes] = await Promise.all([
        axios.get(`${API}/partnerships`, { headers: headers() }),
        axios.get(`${API}/partnerships/pending`, { headers: headers() }),
      ]);
      setPartnerships(activeRes.data);
      setPending(pendingRes.data);
    } catch (err) { console.error('Error loading partnerships:', err); }
    finally { setPartnersLoading(false); }
  };

  const handleAcceptByToken = async (token) => {
    try {
      const pendingRes = await axios.get(`${API}/partnerships/pending`, { headers: headers() });
      const received = pendingRes.data.received || [];
      const match = received.find(p => p.invite_token === token);
      if (match) {
        await axios.post(`${API}/partnerships/${match.id}/accept`, {}, { headers: headers() });
        setSuccess('Partnership accepted successfully!');
        setSearchParams({ tab: 'partners' });
        loadPartnerships();
      } else {
        setError('Partnership invite not found or already processed.');
        setSearchParams({ tab: 'partners' });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept partnership');
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/partnerships/invite`, { email: inviteEmail.trim(), message: inviteMessage.trim() || null }, { headers: headers() });
      setSuccess('Partnership invite sent!');
      setInviteEmail(''); setInviteMessage(''); setShowInviteForm(false);
      loadPartnerships();
    } catch (err) { setError(err.response?.data?.error || 'Failed to send invite'); }
    finally { setInviting(false); }
  };

  const handleAccept = async (id) => {
    try {
      await axios.post(`${API}/partnerships/${id}/accept`, {}, { headers: headers() });
      setSuccess('Partnership accepted!'); loadPartnerships();
    } catch (err) { setError(err.response?.data?.error || 'Failed to accept'); }
  };

  const handleDecline = async (id) => {
    if (!confirm('Decline this partnership invite?')) return;
    try { await axios.post(`${API}/partnerships/${id}/decline`, {}, { headers: headers() }); loadPartnerships(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to decline'); }
  };

  const handleCancelInvite = async (id) => {
    if (!confirm('Cancel this pending invite?')) return;
    try { await axios.delete(`${API}/partnerships/${id}`, { headers: headers() }); loadPartnerships(); }
    catch (err) { setError(err.response?.data?.error || 'Failed to cancel invite'); }
  };

  const handleRevoke = async (id) => {
    if (!confirm('Remove this partner? They will no longer see your searches.')) return;
    try {
      await axios.put(`${API}/partnerships/${id}/revoke`, {}, { headers: headers() });
      setSuccess('Partnership removed.'); loadPartnerships();
    } catch (err) { setError(err.response?.data?.error || 'Failed to remove partner'); }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    if (!formData.name || !formData.email) { setError('Name and email are required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/users/admin/${user.id}`, formData, { headers: headers() });
      const updatedUser = { ...user, ...formData };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setSuccess('Profile updated successfully!'); setEditing(false);
    } catch (err) { setError(err.response?.data?.error || 'Failed to update profile'); }
    finally { setSaving(false); }
  };

  // Avatar upload — reads the picked file as base64, PUTs it to
  // /api/users/me (any authenticated user can update their own avatar,
  // unlike /admin/:userId which is admin-only). Optimistic preview so
  // the new photo appears instantly. Server enforces max size (3MB
  // post-base64); the frontend enforces 2MB pre-encoding for headroom.
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please pick an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2MB'); return; }
    setError('');
    setUploadingAvatar(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result;
      setAvatarPreview(dataUrl);
      try {
        const res = await axios.put(`${API}/users/me`, { avatar_url: dataUrl }, { headers: headers() });
        const updated = { ...user, avatar_url: res.data?.user?.avatar_url || dataUrl };
        localStorage.setItem('user', JSON.stringify(updated));
        setUser(updated);
        setAvatarPreview(null); // now sourced from user.avatar_url
        setSuccess('Photo updated');
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to save photo');
        setAvatarPreview(null); // roll back optimistic preview
      } finally {
        setUploadingAvatar(false);
        if (avatarFileRef.current) avatarFileRef.current.value = '';
      }
    };
    reader.onerror = () => { setError('Failed to read image file'); setUploadingAvatar(false); };
    reader.readAsDataURL(file);
  };

  const handleAvatarRemove = async () => {
    setUploadingAvatar(true);
    setError('');
    try {
      // Empty string on the server side clears avatar_url. Preview is
      // dropped either way so the initials fallback re-appears.
      await axios.put(`${API}/users/me`, { avatar_url: null }, { headers: headers() });
      const updated = { ...user, avatar_url: '' };
      localStorage.setItem('user', JSON.stringify(updated));
      setUser(updated);
      setAvatarPreview(null);
      setSuccess('Photo removed');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) { setError('All password fields are required'); return; }
    if (passwordData.newPassword !== passwordData.confirmPassword) { setError('New passwords do not match'); return; }
    if (passwordData.newPassword.length < 8) { setError('New password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword }, { headers: headers() });
      setSuccess('Password changed successfully!'); setChangingPassword(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) { setError(err.response?.data?.error || 'Failed to change password'); }
    finally { setSaving(false); }
  };

  const getUserTypeLabel = (type) => {
    switch (type) { case 'admin': return 'Administrator'; case 'recruitment': return 'Recruitment'; case 'client': return 'Client'; default: return type; }
  };
  const getUserTypeBadgeColor = (type) => {
    switch (type) { case 'admin': return 'bg-purple-100 text-purple-700'; case 'recruitment': return 'bg-cyan-100 text-cyan-700'; case 'client': return 'bg-green-100 text-green-700'; default: return 'bg-gray-100 text-gray-700'; }
  };

  const canManagePartners = user && (user.company_role === 'company_admin' || user.user_type === 'admin');
  const showPartnersTab = user && (user.user_type === 'client' || user.user_type === 'recruitment' || user.user_type === 'admin');

  if (loading) return (
    <AppShell variant={isClientRoute ? 'client' : 'recruiter'}>
      <div className="h-full overflow-y-auto bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    </AppShell>
  );

  return (
    <AppShell variant={isClientRoute ? 'client' : 'recruiter'}>
      <div className="h-full overflow-y-auto bg-gray-50">
      <div className="py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
        </div>

        {(showPartnersTab || true) && (
          <div className="flex gap-1 mb-6 bg-white rounded-lg shadow p-1">
            {[
              { id: 'profile', label: 'Profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
              ...(showPartnersTab ? [{ id: 'partners', label: 'Search Partners', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' }] : []),
              { id: 'notifications', label: 'Notifications', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
            ].map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setError(''); setSuccess(''); }}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${activeTab === tab.id ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} /></svg>
                {tab.label}
                {tab.id === 'partners' && pending.received?.length > 0 && <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pending.received.length}</span>}
              </button>
            ))}
          </div>
        )}

        {success && <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center justify-between">{success}<button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button></div>}
        {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">{error}<button onClick={() => setError('')} className="text-red-500 hover:text-red-700"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button></div>}

        {activeTab === 'profile' && (<>
          {/* Your Rank — internal-only. Client users don't run outreach
              campaigns so the metric wouldn't be meaningful for them.
              Widget handles its own loading/empty states, so if the
              user has no messages yet it degrades gracefully to "No
              ranking data yet". */}
          {!isClientRoute && (
            <div className="mb-4">
              <ProfileRankWidget />
            </div>
          )}

          <div className="bg-white rounded-lg shadow">
            {/* Profile Header */}
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Avatar — always editable via hover overlay (independent
                    of the name/email Edit Profile flow). Click the
                    camera to swap; click the red ✕ to remove. Both
                    fire immediately, no Save needed. Matches the
                    firm-logo UX pattern where uploads are terminal. */}
                <div className="relative group">
                  {user?.avatar_url || avatarPreview ? (
                    <img
                      src={avatarPreview || user?.avatar_url}
                      alt=""
                      className="w-14 h-14 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-xl font-bold text-white">{user?.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => avatarFileRef.current?.click()}
                    disabled={uploadingAvatar}
                    title="Change photo"
                    className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity text-white disabled:opacity-100"
                  >
                    {uploadingAvatar ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    )}
                  </button>
                  {(user?.avatar_url || avatarPreview) && !uploadingAvatar && (
                    <button
                      type="button"
                      onClick={handleAvatarRemove}
                      title="Remove photo"
                      className="absolute -bottom-1 -right-1 w-5 h-5 bg-white border border-gray-300 rounded-full flex items-center justify-center text-red-500 hover:bg-red-50 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  )}
                  <input
                    ref={avatarFileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleAvatarUpload}
                    className="hidden"
                  />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{user?.name}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getUserTypeBadgeColor(user?.user_type)}`}>{getUserTypeLabel(user?.user_type)}</span>
                    {user?.firm_name && <span className="text-sm text-gray-500">{user.firm_name}</span>}
                  </div>
                </div>
              </div>
              {!editing && <button onClick={() => setEditing(true)} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">Edit Profile</button>}
            </div>

            {/* Profile Edit / Info */}
            <div className="px-6 py-4 border-b">
              {!editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><label className="block text-xs text-gray-500 mb-0.5">Email</label><p className="text-sm text-gray-900">{user?.email}</p></div>
                  <div><label className="block text-xs text-gray-500 mb-0.5">Account Type</label><p className="text-sm text-gray-900">{getUserTypeLabel(user?.user_type)}</p></div>
                </div>
              ) : (
                <form onSubmit={handleUpdateProfile} className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label><input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Email *</label><input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => { setEditing(false); setFormData({name: user.name, email: user.email}); setError(''); }} className="px-3 py-1.5 text-xs bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                    <button type="submit" disabled={saving} className={`px-3 py-1.5 text-xs rounded-lg font-medium ${saving ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>{saving ? 'Saving...' : 'Save'}</button>
                  </div>
                </form>
              )}
            </div>

            {/* Password */}
            <div className="px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <div><h3 className="text-sm font-semibold text-gray-900">Password</h3></div>
                {!changingPassword && <button onClick={() => setChangingPassword(true)} className="px-3 py-1.5 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Change</button>}
              </div>
              {changingPassword && (
                <form onSubmit={handleChangePassword} className="mt-3 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Current *</label><input type="password" value={passwordData.currentPassword} onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">New *</label><input type="password" value={passwordData.newPassword} onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Min 8 chars" /></div>
                    <div><label className="block text-xs font-medium text-gray-700 mb-1">Confirm *</label><input type="password" value={passwordData.confirmPassword} onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500" /></div>
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={saving} className={`px-3 py-1.5 text-xs rounded-lg font-medium ${saving ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>{saving ? 'Changing...' : 'Change Password'}</button>
                    <button type="button" onClick={() => { setChangingPassword(false); setPasswordData({currentPassword:'',newPassword:'',confirmPassword:''}); setError(''); }} className="px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50">Cancel</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </>)}

        {activeTab === 'partners' && (<>
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Search Partners</h2>
                <p className="text-sm text-gray-600 mt-1">{user?.user_type === 'client' ? 'Manage your recruitment firm partnerships. Partners can be assigned to searches.' : 'Manage your client partnerships. Clients can assign you to their searches.'}</p>
              </div>
              {canManagePartners && !showInviteForm && (
                <button onClick={() => setShowInviteForm(true)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                  Invite Partner
                </button>
              )}
            </div>
            {showInviteForm && (
              <div className="p-6 bg-blue-50 border-b">
                <form onSubmit={handleInvite} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Partner's Email Address *</label>
                    <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter the email of someone at the partner firm" autoFocus />
                    <p className="text-xs text-gray-500 mt-1">This should be the email of a user at the {user?.user_type === 'client' ? 'recruitment' : 'client'} firm you want to partner with.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                    <textarea value={inviteMessage} onChange={(e) => setInviteMessage(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" rows={2} placeholder="Add a personal message to the invite..." />
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" disabled={inviting || !inviteEmail.trim()} className={`px-4 py-2 text-sm rounded-lg font-medium ${inviting || !inviteEmail.trim() ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>{inviting ? 'Sending...' : 'Send Invite'}</button>
                    <button type="button" onClick={() => { setShowInviteForm(false); setInviteEmail(''); setInviteMessage(''); }} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {pending.received?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b border-amber-200">
                <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                  Pending Invites ({pending.received.length})
                </h3>
              </div>
              <div className="divide-y divide-amber-200">
                {pending.received.map((invite) => (
                  <div key={invite.id} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{invite.inviting_firm_name || 'Unknown Firm'}</p>
                      <p className="text-sm text-gray-600">Invited by {invite.invited_by_name} — {new Date(invite.created_at).toLocaleDateString()}</p>
                      {invite.message && <p className="text-sm text-gray-500 mt-1 italic">"{invite.message}"</p>}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleAccept(invite.id)} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Accept</button>
                      <button onClick={() => handleDecline(invite.id)} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Decline</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {pending.sent?.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b"><h3 className="text-lg font-semibold text-gray-900">Sent Invites ({pending.sent.length})</h3></div>
              <div className="divide-y">
                {pending.sent.map((invite) => (
                  <div key={invite.id} className="px-6 py-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{invite.invited_firm_name || invite.invited_email}</p>
                      <p className="text-sm text-gray-500">Sent to {invite.invited_email} — {new Date(invite.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">Pending</span>
                      {canManagePartners && <button onClick={() => handleCancelInvite(invite.id)} className="text-sm text-red-600 hover:text-red-700">Cancel</button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h3 className="text-lg font-semibold text-gray-900">Active Partners ({partnerships.length})</h3></div>
            {partnersLoading ? <div className="p-8 text-center text-gray-500">Loading...</div>
            : partnerships.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                <p className="text-gray-500 font-medium">No active partners yet</p>
                <p className="text-sm text-gray-400 mt-1">Invite a {user?.user_type === 'client' ? 'recruitment' : 'client'} firm to get started.</p>
              </div>
            ) : (
              <div className="divide-y">
                {partnerships.map((p) => (
                  <div key={p.id} className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${p.partner_firm_type === 'recruitment' ? 'bg-cyan-100' : 'bg-green-100'}`}>
                        {p.partner_firm_type === 'recruitment'
                          ? <svg className="w-5 h-5 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                          : <svg className="w-5 h-5 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{p.partner_firm_name}</p>
                        <p className="text-sm text-gray-500">
                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs ${p.partner_firm_type === 'recruitment' ? 'bg-cyan-100 text-cyan-700' : 'bg-green-100 text-green-700'}`}>{p.partner_firm_type === 'recruitment' ? 'Recruitment' : 'Client'}</span>
                          <span className="ml-2">Since {new Date(p.accepted_at).toLocaleDateString()}</span>
                        </p>
                      </div>
                    </div>
                    {canManagePartners && <button onClick={() => handleRevoke(p.id)} className="text-sm text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded">Remove</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>)}

        {activeTab === 'notifications' && (<>
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
              <p className="text-xs text-gray-500 mt-0.5">Choose how you receive each notification — instantly, in a digest, or not at all.</p>
            </div>
            <div className="p-6">
              {notifLoading ? (
                <div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
              ) : notifPrefs ? (() => {
                const eventList = notifPrefs.user_type === 'client' ? [
                  { key: 'new_candidate', label: 'New candidates', desc: 'When a recruiter sends you a new candidate' },
                  { key: 'stage_change', label: 'Stage changes', desc: 'When a candidate moves between stages' },
                  { key: 'note_mention', label: '@-mentions in notes', desc: 'When someone tags you in a note (@your-name)' },
                ] : [
                  { key: 'note_mention', label: '@-mentions in notes', desc: 'When a teammate tags you in a candidate note (@your-name)' },
                  { key: 'note_candidate_mention', label: 'Cross-references to your candidates', desc: 'When someone #references a candidate you created on another profile' },
                  { key: 'user_assignment', label: 'Candidate assignments', desc: 'When you are assigned to a candidate' },
                  { key: 'client_rating', label: 'Client ratings', desc: 'When a client rates a candidate' },
                  { key: 'client_status_change', label: 'Approvals & rejections', desc: 'When a client approves or rejects' },
                  { key: 'client_feedback', label: 'Interview feedback', desc: 'When a client submits feedback' },
                  { key: 'client_stage_move', label: 'Client stage moves', desc: 'When a client moves a candidate' },
                  { key: 'new_pipeline_request', label: 'New search requests', desc: 'When a client submits a search' },
                  { key: 'pipeline_submitted', label: 'Pipeline submissions', desc: 'When a search is submitted to a client' },
                ];
                const getMode = (key) => { const v = notifPrefs.event_types?.[key]; if (v === 'instant' || v === 'digest') return v; if (v === true) return 'digest'; return 'off'; };
                const setMode = (key, mode) => saveNotifPrefs({ event_types: { [key]: mode } });
                const hasAnyDigest = eventList.some(e => getMode(e.key) === 'digest');
                const modeColors = { off: 'bg-gray-100 text-gray-400', instant: 'bg-blue-600 text-white', digest: 'bg-indigo-600 text-white' };
                return (
                  <div className="space-y-5">
                    <div className="space-y-2">
                      {eventList.map(evt => (
                        <div key={evt.key} className="flex items-center justify-between py-3 px-4 rounded-lg border border-gray-100 hover:border-gray-200">
                          <div className="flex-1 mr-4">
                            <p className="text-sm font-medium text-gray-800">{evt.label}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{evt.desc}</p>
                          </div>
                          <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
                            {['off', 'instant', 'digest'].map(m => (
                              <button key={m} onClick={() => setMode(evt.key, m)} disabled={notifSaving}
                                className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-all ${getMode(evt.key) === m ? modeColors[m] : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'}`}>
                                {m === 'off' ? 'Off' : m === 'instant' ? 'Instant' : 'Digest'}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {hasAnyDigest && (
                      <div className="border-t pt-4">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1">Digest Schedule</h3>
                        <p className="text-xs text-gray-400 mb-3">Events set to "Digest" are bundled at this time.</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div><label className="block text-xs text-gray-600 mb-1">Frequency</label><select value={notifPrefs.digest_frequency || 'weekly'} onChange={(e) => saveNotifPrefs({ digest_frequency: e.target.value })} disabled={notifSaving} className="w-full px-2 py-1.5 border rounded-lg text-sm"><option value="daily">Daily</option><option value="weekly">Weekly</option></select></div>
                          {(notifPrefs.digest_frequency || 'weekly') === 'weekly' && <div><label className="block text-xs text-gray-600 mb-1">Day</label><select value={notifPrefs.digest_day || 'friday'} onChange={(e) => saveNotifPrefs({ digest_day: e.target.value })} disabled={notifSaving} className="w-full px-2 py-1.5 border rounded-lg text-sm">{['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => <option key={d} value={d.toLowerCase()}>{d}</option>)}</select></div>}
                          <div><label className="block text-xs text-gray-600 mb-1">Time</label><select value={notifPrefs.digest_time || '09:00'} onChange={(e) => saveNotifPrefs({ digest_time: e.target.value })} disabled={notifSaving} className="w-full px-2 py-1.5 border rounded-lg text-sm">{Array.from({length:24},(_,i) => { const h=String(i).padStart(2,'0'); const l=i===0?'12 AM':i<12?`${i} AM`:i===12?'12 PM':`${i-12} PM`; return <option key={h} value={`${h}:00`}>{l}</option>; })}</select></div>
                          <div><label className="block text-xs text-gray-600 mb-1">Timezone</label><select value={notifPrefs.digest_timezone || 'America/New_York'} onChange={(e) => saveNotifPrefs({ digest_timezone: e.target.value })} disabled={notifSaving} className="w-full px-2 py-1.5 border rounded-lg text-sm">{[{v:'America/New_York',l:'Eastern'},{v:'America/Chicago',l:'Central'},{v:'America/Denver',l:'Mountain'},{v:'America/Los_Angeles',l:'Pacific'},{v:'Europe/London',l:'London'},{v:'Europe/Paris',l:'CET'},{v:'Asia/Tokyo',l:'Tokyo'},{v:'Australia/Sydney',l:'Sydney'}].map(tz => <option key={tz.v} value={tz.v}>{tz.l}</option>)}</select></div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })() : <p className="text-gray-500 text-sm">Unable to load preferences.</p>}
            </div>
          </div>
        </>)}

        {/* Integrations — only for internal users */}
        {activeTab === 'profile' && !isClientRoute && (
          <div className="bg-white rounded-lg shadow mt-6">
            {/* LinkedIn */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">LinkedIn Sync Extension <span className="ml-1.5 text-[10px] font-medium text-green-700 bg-green-100 px-1.5 py-0.5 rounded">v2.1</span></p>
                  <p className="text-xs text-gray-500">Scan profiles, sync candidates, tag inline, and use the web-app overlay from your sidebar</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href="/extension/banyak-linkedin-extension.zip" download className="px-3 py-1.5 bg-green-700 text-white text-xs font-medium rounded-lg hover:bg-green-800">Download v2.1</a>
                <a href="/extension.html" target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50">Install Guide</a>
              </div>
            </div>

            {/* Calendar */}
            <div className="px-6 py-4 border-b">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Calendar & Email</h3>
              <p className="text-xs text-gray-500 mb-3">Connect for calendar sync and outreach emails</p>
              <div className="space-y-2">
                {/* Google Calendar */}
                {(() => {
                  const gc = calendarConnections.find(c => c.provider === 'google');
                  return gc ? (
                    <div className="flex items-center justify-between px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                        <div>
                          <p className="text-xs font-medium text-green-800">Google Calendar</p>
                          <p className="text-[10px] text-green-600">{gc.calendar_email}</p>
                        </div>
                        <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">Connected</span>
                      </div>
                      <button onClick={() => disconnectCalendar('google')} className="text-[10px] text-red-500 hover:text-red-700 px-2 py-1 border border-red-200 rounded hover:bg-red-50">Disconnect</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                        <div><p className="text-xs font-medium text-gray-700">Google Calendar</p><p className="text-[10px] text-gray-500">Google Meet links</p></div>
                      </div>
                      <button onClick={() => connectCalendar('google')} disabled={connectingCalendar === 'google'} className="text-[10px] font-medium text-blue-600 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50">{connectingCalendar === 'google' ? 'Connecting...' : 'Connect'}</button>
                    </div>
                  );
                })()}
                {/* Microsoft */}
                {(() => {
                  const mc = calendarConnections.find(c => c.provider === 'microsoft');
                  return mc ? (
                    <div className="flex items-center justify-between px-3 py-2.5 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24"><rect fill="#F25022" x="1" y="1" width="10" height="10"/><rect fill="#7FBA00" x="13" y="1" width="10" height="10"/><rect fill="#00A4EF" x="1" y="13" width="10" height="10"/><rect fill="#FFB900" x="13" y="13" width="10" height="10"/></svg>
                        <div>
                          <p className="text-xs font-medium text-green-800">Microsoft Outlook</p>
                          <p className="text-[10px] text-green-600">{mc.calendar_email}</p>
                        </div>
                        <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">Connected</span>
                      </div>
                      <button onClick={() => disconnectCalendar('microsoft')} className="text-[10px] text-red-500 hover:text-red-700 px-2 py-1 border border-red-200 rounded hover:bg-red-50">Disconnect</button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24"><rect fill="#F25022" x="1" y="1" width="10" height="10"/><rect fill="#7FBA00" x="13" y="1" width="10" height="10"/><rect fill="#00A4EF" x="1" y="13" width="10" height="10"/><rect fill="#FFB900" x="13" y="13" width="10" height="10"/></svg>
                        <div><p className="text-xs font-medium text-gray-700">Microsoft Outlook</p><p className="text-[10px] text-gray-500">Teams links</p></div>
                      </div>
                      <button onClick={() => connectCalendar('microsoft')} disabled={connectingCalendar === 'microsoft'} className="text-[10px] font-medium text-blue-600 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50 disabled:opacity-50">{connectingCalendar === 'microsoft' ? 'Connecting...' : 'Connect'}</button>
                    </div>
                  );
                })()}

                {/* Organization-wide consent (admin only) */}
                {(user?.company_role === 'company_admin' || user?.user_type === 'admin') && (
                  <div className="mt-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-xs font-medium text-blue-900">Approve Microsoft for your whole organization</p>
                        <p className="text-[10px] text-blue-600 mt-0.5">One-time admin approval so your team can connect without individual admin prompts.</p>
                      </div>
                      <button onClick={grantOrgConsent} disabled={connectingOrg} className="shrink-0 text-[10px] font-medium text-white bg-blue-600 px-2.5 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50">{connectingOrg ? 'Redirecting...' : 'Approve for org'}</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Firm Branding */}
            <div className="px-6 py-4 border-b">
              <FirmBranding />
            </div>

            {/* Phone & SMS */}
            <div className="px-6 py-4">
              <PhoneNumberManager />
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Legal Links */}
      <div className="max-w-2xl mx-auto px-4 pb-8">
        <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 hover:underline">Privacy Policy</a>
          <span>·</span>
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 hover:underline">Terms of Service</a>
        </div>
      </div>
    </div>
    </AppShell>
  );
};

export default UserProfile;
